import { formatters } from '@seek/ca-myprofile-utils';
import { DefinitionList } from '@seek/profile-base-ui';
import { getCountryCallingCodeById } from '@seek/profile-taxonomies';
import { useTranslations } from '@vocab/react';
import { Stack } from 'braid-design-system';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Card } from 'src/components/Card/Card';
import { CardAction } from 'src/components/Card/CardAction/CardAction';
import { FormDrawer } from 'src/components/FormDrawer/FormDrawer';
import { LoadingItem } from 'src/components/LoadingBlock/LoadingBlock';
import { PERSONAL_DETAILS } from 'src/components/Router';
import { EDIT } from 'src/consts';
import { PERSONAL_DETAILS_FORM } from 'src/context/FormContext';
import { analyticsClient } from 'src/modules/analytics';
import { retryParent } from 'src/modules/redux-blend';

import { EditPersonalDetails } from '../../PersonalDetails/EditPersonalDetails/EditPersonalDetails';
import { usePersonalDetails } from '../../PersonalDetails/data';

import translations from './.vocab';

export const SimplePersonalDetails = () => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();
  const [countryCallingCodePrefix, setCountryCallingCodePrefix] = useState('');
  const { personalDetails, loading, error } = usePersonalDetails();

  const firstName = personalDetails?.personalDetails.firstName || '';
  const lastName = personalDetails?.personalDetails.lastName || '';
  const phoneNumber = personalDetails?.personalDetails.phoneNumber;
  const location = personalDetails?.currentLocation2?.shortDescription || '';
  const emailAddress = personalDetails?.emailAddress;
  const countryCallingCode =
    personalDetails?.personalDetails.countryCallingCode;

  const name = formatters.formatName(firstName, lastName);

  useEffect(() => {
    if (countryCallingCode?.id) {
      getCountryCallingCodeById(countryCallingCode.id).then((prefix) => {
        setCountryCallingCodePrefix(`+${prefix}`);
      });
    }
  }, [countryCallingCode]);

  const retryAction = useCallback(
    () => dispatch(retryParent(PERSONAL_DETAILS)),
    [dispatch],
  );

  const isLoading = Boolean(loading || error);

  return (
    <>
      <Card
        name={PERSONAL_DETAILS}
        retryAction={retryAction}
        title={t('Personal details')}
        isEmpty={false}
        loading={isLoading}
        loader={
          <Stack space="large">
            <LoadingItem type="text" size="standard" width="large" />
            <LoadingItem type="text" size="standard" width="large" />
            <LoadingItem type="text" size="standard" width="large" />
            <LoadingItem type="text" size="standard" width="large" />
            <LoadingItem type="button" />
          </Stack>
        }
      >
        <Stack space="large">
          <DefinitionList
            data={[
              {
                name: t('Name'),
                values: [name],
              },
              {
                name: t('Phone number'),
                values: [
                  phoneNumber
                    ? `${countryCallingCodePrefix}${phoneNumber}`
                    : '',
                ],
              },
              {
                name: t('Lives in'),
                values: [location],
              },
              {
                name: t('Email'),
                values: [emailAddress],
              },
            ]}
            space="medium"
          />
          <CardAction
            card={PERSONAL_DETAILS}
            mode={EDIT}
            data={{ automation: 'personal-details-edit' }}
            actionText={t('Edit personal details')}
            onClick={() => {
              analyticsClient.cardOpened({
                currentPageSection: 'personal details',
                currentPageSectionState: 'edit',
              });
            }}
          />
        </Stack>
      </Card>
      <FormDrawer
        title={t('Edit personal details')}
        component={EditPersonalDetails}
        card={PERSONAL_DETAILS}
        formName={PERSONAL_DETAILS_FORM}
      />
    </>
  );
};
